import { useEffect } from 'react';

import { useAddressInputContext } from '@perpay-web/components/base/AddressInput/useAddressInputContext';
import FormikPhoneInput from '@perpay-web/components/base/PhoneInput/FormikPhoneInput';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import Inline from '@perpay-web/components/design-system/Inline/Inline';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import IdentityDetailsBlock from '@perpay-web/fintech/components/base/IdentityDetailsBlock/IdentityDetailsBlock';
import { PhoneDisclosure } from '@perpay-web/fintech/components/composite/PhoneNumberForm/PhoneDisclosure';
import AddressInputContainer from '@perpay-web/fintech/components/containers/AddressInputContainer';
import { useCardApplicationContext } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import useEmailMisspelled from '@perpay-web/fintech/hooks/useEmailMisspelled';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';

import styles from './CardApplicationPersonalDetailsForm.scss';

/**
 * Card Application Personal Details Form Component
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance
 * @param {Object} [props.errors={}] - Form errors object
 * @param {boolean} [props.isUserIdentityVerified=false] - Whether user identity is verified
 * @param {boolean} [props.isExistingUser=false] - Whether user is existing
 * @param {Object} props.initialAddressValues - Initial address values
 * @param {string} [props.dateOfBirth=''] - Date of birth
 * @param {string} [props.firstName=''] - First name
 * @param {string} [props.lastName=''] - Last name
 * @param {string} [props.ssn=''] - Social security number
 * @param {string} [props.phoneNumber=''] - Phone number
 * @param {string} [props.addressInputText] - Address input text (optional)
 * @returns {JSX.Element} Component JSX
 */
const CardApplicationPersonalDetailsForm = ({
    formik,
    errors = {},
    isUserIdentityVerified = false,
    isExistingUser = false,
    initialAddressValues,
    dateOfBirth = '',
    firstName = '',
    lastName = '',
    ssn = '',
    phoneNumber = '',
    addressInputText,
}) => {
    const { showPhoneDisclosure, setShowPhoneDisclosure } =
        useCardApplicationContext();

    const { checkEmail } = useEmailMisspelled();

    const { setShowDetailedForm } = useAddressInputContext();

    useEffect(() => {
        if (initialAddressValues.street1) {
            setShowDetailedForm(true);
        }
    }, [initialAddressValues, setShowDetailedForm]);

    return (
        <Stack className={styles['outer-stack']}>
            {isUserIdentityVerified ? (
                <Stack className={styles['inner-stack']}>
                    <div className={styles.section__title}>
                        Review personal information
                    </div>
                    <IdentityDetailsBlock
                        firstName={firstName}
                        lastName={lastName}
                        dateOfBirth={dateOfBirth}
                        ssn={ssn}
                    />
                </Stack>
            ) : (
                <Stack className={styles['inner-stack']}>
                    <Inline className={styles['input-row']}>
                        <EndToEndTestIdProvider testId='pho-first-name'>
                            <FormikTextInput
                                name='firstName'
                                label='First Name'
                                autoComplete='given-name'
                                errors={errors}
                            />
                        </EndToEndTestIdProvider>
                        <EndToEndTestIdProvider testId='pho-last-name'>
                            <FormikTextInput
                                name='lastName'
                                label='Last Name'
                                autoComplete='family-name'
                                errors={errors}
                            />
                        </EndToEndTestIdProvider>
                    </Inline>

                    {!isExistingUser ? (
                        <EndToEndTestIdProvider testId='pho-email'>
                            <FormikTextInput
                                name='email'
                                label='Email'
                                autoComplete='email'
                                errors={errors}
                                onBlur={() => checkEmail(formik.values.email)}
                            />
                        </EndToEndTestIdProvider>
                    ) : null}
                    {!phoneNumber ? (
                        <div>
                            <EndToEndTestIdProvider testId='pho-phone-number'>
                                <FormikPhoneInput
                                    name='phoneNumber'
                                    label='Phone Number'
                                    errors={errors}
                                    numbersOnly
                                    onFocus={() => {
                                        setShowPhoneDisclosure(true);
                                    }}
                                />
                            </EndToEndTestIdProvider>
                            {showPhoneDisclosure ? (
                                <PhoneDisclosure
                                    className={styles['phone-disclosure']}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </Stack>
            )}

            {/* Shared Address Section */}
            <Stack className={styles['inner-stack']}>
                {isUserIdentityVerified ? (
                    <div className={styles.section__address__title}>
                        Add address
                    </div>
                ) : null}
                <AddressInputContainer addressInputText={addressInputText} />
            </Stack>
        </Stack>
    );
};

export default CardApplicationPersonalDetailsForm;
